import React, { useEffect, useState } from "react";
import { Helmet } from "react-helmet-async";
import { Link } from "react-router-dom";
import { ArrowRight, ShieldCheck, Clock, MapPin, Wind, Droplet, Zap, Sparkles, Hammer, Bug, Scissors } from "lucide-react";
import { Button } from "../components/ui/button";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import { api } from "../lib/api";
import Seo from "../components/Seo";
import JsonLd from "../components/JsonLd";

const iconMap = {
  MapPin, Clock, ShieldCheck,
  wind: Wind, droplet: Droplet, zap: Zap, sparkles: Sparkles,
  "paint-bucket": Sparkles, hammer: Hammer, bug: Bug, scissors: Scissors,
  wrench: Hammer,
};

const StepIcon = ({ name }) => {
  const Icon = iconMap[name] || MapPin;
  return <Icon size={24} strokeWidth={1.5} />;
};

const FALLBACK = {
  hero: { badge: "", title: "", description: "", imageUrl: "", imageCaption: "", prosOnlineCount: "", prosOnlineLabel: "" },
  stats: { bookingsCount: "", bookingsLabel: "", ratingValue: "", ratingLabel: "" },
  howItWorks: { badge: "", title: "", steps: [] },
  services: { badge: "", title: "", description: "" },
  cta: { badge: "", title: "", description: "", buttonText: "", image1Url: "", image2Url: "" },
  testimonials: { badge: "", title: "", items: [] },
};

const DEFAULT_STEP = { icon: "MapPin", imageUrl: "", title: "", description: "" };

const CACHE_TTL = 2 * 60 * 1000;

function cacheRead(key) {
  try {
    const raw = localStorage.getItem(key);
    if (!raw) return null;
    return JSON.parse(raw).data;
  } catch { return null; }
}

function cacheWrite(key, data) {
  try { localStorage.setItem(key, JSON.stringify({ data, ts: Date.now() })); } catch {}
}

function isFresh(key) {
  try {
    const raw = localStorage.getItem(key);
    if (!raw) return false;
    return Date.now() - JSON.parse(raw).ts < CACHE_TTL;
  } catch { return false; }
}

function mergeContent(raw) {
  const merged = {};
  for (const key of Object.keys(FALLBACK)) {
    merged[key] = { ...FALLBACK[key], ...(raw[key] || {}) };
  }
  if (merged.howItWorks.steps) {
    merged.howItWorks.steps = merged.howItWorks.steps.map(s => ({ ...DEFAULT_STEP, ...s }));
  }
  return merged;
}

const CONTENT_KEY = "hg_content_cache";
const SERVICES_KEY = "hg_services_cache";

export default function Landing() {
  const [services, setServices] = useState(() => cacheRead(SERVICES_KEY) || []);
  const [content, setContent] = useState(() => {
    const cached = cacheRead(CONTENT_KEY);
    return cached ? mergeContent(cached) : FALLBACK;
  });
  const [heroLoaded, setHeroLoaded] = useState(false);

  // Preconnect to backend
  useEffect(() => {
    const backend = process.env.REACT_APP_BACKEND_URL;
    if (!backend) return;
    for (const rel of ["preconnect", "dns-prefetch"]) {
      const link = document.createElement("link");
      link.rel = rel;
      link.href = backend;
      document.head.appendChild(link);
    }
  }, []);

  // Listen for cache clears from AdminCMS (cross-tab)
  useEffect(() => {
    const handler = () => {
      const fresh = isFresh(CONTENT_KEY);
      if (!fresh) {
        api.get("/content").then(r => { cacheWrite(CONTENT_KEY, r.data); setContent(mergeContent(r.data)); }).catch(() => {});
      }
    };
    window.addEventListener("storage", handler);
    return () => window.removeEventListener("storage", handler);
  }, []);

  useEffect(() => {
    const freshContent = isFresh(CONTENT_KEY);
    const freshServices = isFresh(SERVICES_KEY);
    if (freshContent && freshServices) return;

    Promise.all([
      !freshServices ? api.get("/services").then(r => { const d = r.data.slice(0, 6); cacheWrite(SERVICES_KEY, d); return d; }).catch(() => null) : Promise.resolve(null),
      !freshContent ? api.get("/content").then(r => { cacheWrite(CONTENT_KEY, r.data); return mergeContent(r.data); }).catch(() => null) : Promise.resolve(null),
    ]).then(([svc, cnt]) => {
      if (svc) setServices(svc);
      if (cnt) setContent(cnt);
    });
  }, []);

  const h = content.hero;
  const s = content.stats;
  const hiw = content.howItWorks;

  return (
    <>
      <Seo />
      <JsonLd />
      <div className="min-h-screen flex flex-col bg-[#FAF9F6]">
      <Navbar />

      {/* HERO */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 bg-[linear-gradient(180deg,#FAF9F6_0%,#F3F1ED_100%)]" />
        <div className="relative max-w-7xl mx-auto px-6 lg:px-12 pt-12 lg:pt-20 pb-16">
        <div className="grid grid-cols-12 gap-4 lg:gap-6">
          <div className="col-span-12 lg:col-span-8 flex flex-col justify-end pt-8 lg:pt-16 fade-up">
            <div className="mb-6 flex items-center gap-3">
              <span className="h-12 w-12 overflow-hidden rounded-full bg-black shadow-[0_14px_30px_rgba(0,0,0,0.18)]">
                <img src="/theuniversaltree-logo.jpeg" alt="Universal Tree logo" className="h-full w-full object-cover rounded-full" />
              </span>
              <p className="text-xs uppercase tracking-[0.25em] text-[#D9734E] font-bold">{h.badge}</p>
            </div>
            <h1 className="font-display font-light tracking-tight text-[#1A1D1A] text-5xl sm:text-6xl lg:text-7xl leading-[0.95]">
              {h.title}
            </h1>
            <p className="mt-8 max-w-xl text-base text-[#646964] leading-relaxed">
              {h.description}
            </p>
            <div className="mt-10 flex flex-wrap gap-4">
              <Link to="/services" data-testid="hero-cta-services">
                <Button className="rounded-sm bg-[#1C4532] hover:bg-[#143324] text-white px-7 py-6 text-sm tracking-wide">
                  Browse Services <ArrowRight size={16} className="ml-2" />
                </Button>
              </Link>
              <Link to="/signup?role=worker" data-testid="hero-cta-worker">
                <Button variant="outline" className="rounded-sm border-[#1A1D1A] text-[#1A1D1A] hover:bg-[#1A1D1A] hover:text-white px-7 py-6 text-sm tracking-wide">
                  Become a Pro
                </Button>
              </Link>
            </div>
          </div>

          <div className="col-span-12 lg:col-span-4 row-span-2 fade-up" style={{ animationDelay: "100ms" }}>
            <div className="relative h-full min-h-[420px] lg:min-h-[560px] rounded-sm overflow-hidden shadow-[0_24px_60px_rgba(28,69,50,0.16)] soft-float bg-[#E2E0D9]">
              <img
                src={h.imageUrl}
                alt={h.imageCaption || "Universal Tree home services hero"}
                fetchpriority="high"
                onLoad={() => setHeroLoaded(true)}
                className={`absolute inset-0 w-full h-full object-cover transition-opacity duration-500 ${heroLoaded ? "opacity-100" : "opacity-0"}`}
              />
              <div className="absolute inset-0 bg-gradient-to-tr from-[#1C4532]/50 to-transparent" />
              <div className="absolute bottom-6 left-6 right-6 text-white">
                <div className="flex items-center gap-2 text-xs uppercase tracking-[0.2em] mb-3">
                  <span className="w-2 h-2 bg-[#22C55E] rounded-full animate-pulse"></span>
                  {h.prosOnlineCount} {h.prosOnlineLabel}
                </div>
                <p className="font-display text-2xl leading-snug">{h.imageCaption}</p>
              </div>
            </div>
          </div>

          <div className="col-span-6 lg:col-span-4 bg-[#1C4532] text-white rounded-sm p-6 lg:p-8 fade-up" style={{ animationDelay: "200ms" }}>
            <p className="font-display text-5xl lg:text-6xl font-light">{s.bookingsCount}</p>
            <p className="mt-2 text-sm text-[#FAF9F6]/70">{s.bookingsLabel}</p>
          </div>
          <div className="col-span-6 lg:col-span-4 border border-[#E2E0D9] rounded-sm p-6 lg:p-8 fade-up" style={{ animationDelay: "260ms" }}>
            <p className="font-display text-5xl lg:text-6xl font-light text-[#1A1D1A]">{s.ratingValue}</p>
            <div className="mt-2 text-sm text-[#646964]">{s.ratingLabel}</div>
          </div>
        </div>
        </div>
      </section>

      {/* HOW IT WORKS */}
      <section className="bg-[#F3F1ED] relative overflow-hidden py-20 lg:py-28">
        <div className="absolute inset-0 opacity-[0.03] pointer-events-none" style={{backgroundImage: 'radial-gradient(circle at 25% 25%, #1C4532 1px, transparent 1px)', backgroundSize: '40px 40px'}} />
        <div className="relative max-w-7xl mx-auto px-6 lg:px-12">
          <div className="text-center mb-16">
            <p className="inline-flex items-center gap-2 rounded-sm bg-[#FFF3ED] px-4 py-2 text-xs font-bold uppercase tracking-[0.22em] text-[#D9734E]">{hiw.badge}</p>
            <h2 className="font-display text-4xl lg:text-5xl font-light tracking-tight text-[#1A1D1A] mt-5">{hiw.title}</h2>
          </div>
          <div className="relative grid md:grid-cols-3 gap-8 lg:gap-12">
            {/* Curved connecting line */}
            <div className="hidden md:block absolute top-[20%] left-[15%] right-[15%] h-px bg-[#D9734E]/20" aria-hidden />
            <div className="hidden md:block absolute top-[20%] left-[calc(50%/3)] w-[calc(100%/3)] h-6 border-t-2 border-r-2 border-[#D9734E]/20 rounded-tr-2xl" aria-hidden style={{left: 'calc(16.66% + 20px)', width: 'calc(33.33% - 40px)'}} />
            <div className="hidden md:block absolute top-[20%] left-[calc(50%+20px)] w-[calc(100%/3-40px)] h-6 border-t-2 border-l-2 border-[#D9734E]/20 rounded-tl-2xl" aria-hidden />
            {(hiw.steps || []).map((step, i) => (
              <div key={i} className="group relative fade-up" style={{ animationDelay: `${i * 150}ms` }}>
                <div className="relative aspect-[4/3] rounded-sm overflow-hidden bg-[#E2E0D9] shadow-[0_8px_30px_rgba(28,69,50,0.08)] transition-shadow duration-500 group-hover:shadow-[0_16px_50px_rgba(28,69,50,0.16)]">
                  {step.imageUrl ? (
                    <img src={step.imageUrl} alt="" loading="lazy" className="h-full w-full object-cover transition-all duration-700 group-hover:scale-105" />
                  ) : (
                    <div className="h-full w-full bg-gradient-to-br from-[#1C4532]/10 to-[#D9734E]/10 flex items-center justify-center">
                      <div className="text-[#1C4532]/20"><StepIcon name={step.icon} /></div>
                    </div>
                  )}
                  <div className="absolute inset-0 bg-gradient-to-t from-[#1A1D1A]/60 via-transparent to-transparent opacity-60 transition-opacity duration-500 group-hover:opacity-80" />
                  <div className="absolute top-4 left-4 w-10 h-10 rounded-full bg-white/95 shadow-lg flex items-center justify-center text-sm font-bold text-[#1C4532] backdrop-blur-sm">
                    {i + 1}
                  </div>
                  <div className="absolute bottom-4 left-4 right-4">
                    <div className="inline-flex items-center gap-2 rounded-full bg-white/20 backdrop-blur-md px-3 py-1.5 text-xs text-white font-semibold">
                      <span className="text-white"><StepIcon name={step.icon} /></span>
                      <span>{step.icon === "MapPin" ? "Location" : step.icon === "Clock" ? "Matching" : "Payment"}</span>
                    </div>
                  </div>
                </div>
                <div className="mt-6 pl-1">
                  <p className="font-display text-xl text-[#1A1D1A] group-hover:text-[#D9734E] transition-colors duration-300">{step.title}</p>
                  <p className="text-sm text-[#646964] mt-2 leading-relaxed">{step.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* SERVICES */}
      <section className="relative overflow-hidden py-20">
        <div className="absolute inset-x-0 top-0 h-2/3 bg-[#FAF9F6]" />
        <div className="absolute inset-x-0 bottom-0 h-1/2 bg-[#F3F1ED]" />
        <div className="relative max-w-7xl mx-auto px-6 lg:px-12">
        <div className="mb-10 flex flex-col gap-6 md:flex-row md:items-end md:justify-between">
          <div className="max-w-2xl">
            <p className="mb-3 inline-flex rounded-sm bg-[#FFF3ED] px-3 py-2 text-xs font-bold uppercase tracking-[0.22em] text-[#D9734E]">{content.services.badge}</p>
            <h2 className="font-display text-4xl font-light tracking-tight text-[#1A1D1A] lg:text-5xl">{content.services.title}</h2>
            <p className="mt-4 max-w-xl text-sm leading-relaxed text-[#646964]">
              {content.services.description}
            </p>
          </div>
          <Link to="/services" className="hidden items-center gap-2 rounded-sm border border-[#1C4532]/25 bg-white/70 px-4 py-3 text-sm font-semibold text-[#1C4532] shadow-sm transition-all hover:border-[#1C4532] hover:bg-[#1C4532] hover:text-white md:flex">
            View all <ArrowRight size={16}/>
          </Link>
        </div>
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3 lg:gap-6">
          {services.map((s) => {
            const Icon = iconMap[s.icon] || Hammer;
            return (
              <Link
                key={s.id}
                to={`/services?focus=${s.id}`}
                data-testid={`service-card-${s.id}`}
                className="group relative overflow-hidden rounded-sm border border-[#E2E0D9] bg-white shadow-[0_14px_34px_rgba(28,69,50,0.06)] transition-all hover:-translate-y-1 hover:border-[#1C4532]/70 hover:shadow-[0_18px_45px_rgba(28,69,50,0.12)]"
              >
                <div className="relative aspect-[16/10] overflow-hidden bg-[#F3F1ED]">
                  <img src={s.image_url} alt={s.name} loading="lazy" className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"/>
                  <div className="absolute inset-0 bg-gradient-to-t from-[#1A1D1A]/42 via-transparent to-transparent opacity-80" />
                  <span className="absolute left-4 top-4 rounded-sm bg-white/92 px-3 py-1 text-xs font-bold uppercase tracking-wider text-[#1C4532] shadow-sm">{s.category}</span>
                </div>
                <div className="p-5">
                  <div className="flex items-start justify-between gap-4">
                    <span className="grid h-11 w-11 shrink-0 place-items-center rounded-sm bg-[#F3F1ED] text-[#1C4532] transition-colors group-hover:bg-[#1C4532] group-hover:text-white">
                      <Icon size={20} strokeWidth={1.5}/>
                    </span>
                    <span className="rounded-sm bg-[#FFF3ED] px-3 py-1.5 text-xs font-semibold text-[#D9734E]">From ₹{s.base_price}</span>
                  </div>
                  <p className="mt-4 font-display text-xl text-[#1A1D1A]">{s.name}</p>
                  <p className="mt-2 line-clamp-2 min-h-[40px] text-sm leading-relaxed text-[#646964]">{s.description}</p>
                  <div className="mt-5 flex items-center text-sm font-semibold text-[#1C4532]">
                    Book now <ArrowRight size={15} className="ml-2 transition-transform group-hover:translate-x-1"/>
                  </div>
                </div>
              </Link>
            );
          })}
        </div>
        <Link to="/services" className="mt-8 flex items-center justify-center gap-2 rounded-sm bg-[#1C4532] px-4 py-3 text-sm font-semibold text-white shadow-[0_10px_22px_rgba(28,69,50,0.16)] md:hidden">
          View all services <ArrowRight size={16}/>
        </Link>
        </div>
      </section>

      {/* TESTIMONIALS */}
      <section className="max-w-7xl mx-auto px-6 lg:px-12 pb-24">
        <div className="mb-12 text-center">
          <p className="mb-3 inline-flex rounded-sm bg-[#FFF3ED] px-3 py-2 text-xs font-bold uppercase tracking-[0.22em] text-[#D9734E]">{content.testimonials.badge}</p>
          <h2 className="font-display text-4xl lg:text-5xl font-light tracking-tight text-[#1A1D1A] mt-2">{content.testimonials.title}</h2>
        </div>
        {(content.testimonials.items || []).length > 0 ? (
          <div className="grid md:grid-cols-3 gap-6">
            {(content.testimonials.items || []).map((item, i) => (
              <div key={i} className="bg-white rounded-sm border border-[#E2E0D9] p-8 shadow-[0_4px_14px_rgba(0,0,0,0.04)] flex flex-col transition-all duration-300 hover:-translate-y-1 hover:shadow-[0_16px_40px_rgba(28,69,50,0.10)] hover:border-[#1C4532]/20">
                <div className="flex gap-1 mb-4">
                  {[1,2,3,4,5].map(s => (
                    <span key={s} className={`text-lg ${s <= (item.rating || 5) ? "text-[#D9734E]" : "text-[#E2E0D9]"}`}>★</span>
                  ))}
                </div>
                <p className="text-sm text-[#646964] leading-relaxed flex-1 italic">"{item.text}"</p>
                <div className="flex items-center gap-3 mt-6 pt-4 border-t border-[#E2E0D9]">
                  {item.avatar ? (
                    <img src={item.avatar} alt="" className="w-10 h-10 rounded-full object-cover" />
                  ) : (
                    <div className="w-10 h-10 rounded-full bg-[#1C4532] flex items-center justify-center text-white text-sm font-bold">{item.name?.charAt(0) || "?"}</div>
                  )}
                  <div>
                    <p className="text-sm font-semibold text-[#1A1D1A]">{item.name}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="text-center text-sm text-[#646964] py-12">
            <p>No testimonials yet. Add some from the admin panel.</p>
          </div>
        )}
      </section>

      {/* CTA */}
      <section className="max-w-7xl mx-auto px-6 lg:px-12 pb-24">
        <div className="bg-[#1C4532] text-white rounded-sm p-10 lg:p-16 grid md:grid-cols-2 gap-10 items-center relative overflow-hidden">
          <div>
            <p className="text-xs uppercase tracking-[0.25em] text-[#D9734E] font-bold mb-4">{content.cta.badge}</p>
            <h3 className="font-display text-3xl lg:text-5xl font-light leading-tight">{content.cta.title}</h3>
            <p className="mt-6 text-[#FAF9F6]/70 max-w-md">{content.cta.description}</p>
            <Link to="/signup?role=worker" className="inline-block mt-8">
              <Button className="rounded-sm bg-[#D9734E] hover:bg-[#C26240] text-white px-7 py-6 text-sm tracking-wide">
                {content.cta.buttonText} <ArrowRight size={16} className="ml-2"/>
              </Button>
            </Link>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <img src={content.cta.image1Url} alt="cta" loading="lazy" className="rounded-sm aspect-[3/4] object-cover"/>
            <img src={content.cta.image2Url} alt="cta" loading="lazy" className="rounded-sm aspect-[3/4] object-cover mt-8"/>
          </div>
        </div>
      </section>

      <Footer />
    </div>
    </>
  );
}
